import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Divider,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Tooltip,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ProposeSessionModal from './ProposeSessionModal';

const ClusterInsightsExpanded = ({ cluster, onClose }) => {
    const [proposeModalOpen, setProposeModalOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const timeSlots = ['6–9am', '9–12pm', '12–3pm', '3–6pm', '6–9pm'];
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const heatmapData = {
        'Mon': [2, 4, 1, 0, 3],
        'Tue': [6, 7, 3, 2, 1],
        'Wed': [5, 2, 0, 1, 0],
        'Thu': [3, 5, 2, 4, 1],
        'Fri': [1, 1, 0, 0, 0],
        'Sat': [0, 2, 5, 8, 3],
        'Sun': [0, 1, 1, 2, 5],
    };

    const getBlockColor = (val) => {
        if (val >= 7) return '#12c099';
        if (val >= 4) return '#19795b';
        if (val >= 1) return '#2d3c38';
        return '#111';
    };

    const dummyUsers = [
        { name: 'J. Smith', days: 'Tue/Thu', time: '6–9am', source: 'Instagram', location: '91107' },
        { name: 'A. Chen', days: 'Mon/Wed', time: '5–7pm', source: 'Facebook', location: '91106' },
        { name: 'M. Davis', days: 'Sat', time: '10am–1pm', source: 'Google', location: '91107' }
    ];

    const dummyVenues = [
        { name: 'Glendale Rec Center', distance: '1.8 mi', capacity: 45, rating: '4.6 ⭐', rate: '$55–$85/hr' },
        { name: 'Pasadena YMCA', distance: '2.3 mi', capacity: 30, rating: 'n/a', rate: '$45–$60/hr' },
        { name: 'Marshall HS Gym', distance: '2.9 mi', capacity: 60, rating: '4.2 ⭐', rate: '$70–$95/hr' }
    ];

    const coachesNearby = 12;
    const managersNearby = 3;

    if (!cluster) return null;

    return (
        <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2500,
            overflow: 'auto',
        }}>
            <Box sx={{
                backgroundColor: '#111',
                color: '#fff',
                padding: '1.5rem',
                borderRadius: 3,
                width: isMobile ? '90vw' : '750px',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: 6,
                border: '1px solid #333'
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                        <Typography variant={isMobile ? 'h6' : 'h5'}>
                            Cluster: {cluster.cityName || cluster.name}
                        </Typography>
                        <Typography variant="body2" color="gray">
                            Zip Code: {cluster.zipCodes?.[0] || 'N/A'}
                        </Typography>
                    </Box>
                    <Button onClick={onClose} variant="contained" color="error" size={isMobile ? 'small' : 'medium'}>
                        Close
                    </Button>
                </Box>

                <Box mb={2}>
                    <Typography variant="subtitle1" sx={{ color: '#12c099' }}>
                        🧠 AI Score: <strong>{cluster.activationScore || '93%'}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        This cluster shows strong weekday morning preference overlap. Suggested session:{' '}
                        <strong>Tuesday 6–9am</strong>. Nearest match: <em>Glendale Rec Center</em>.
                    </Typography>
                </Box>

                <Box mb={4}>
                    <Typography variant="h6" gutterBottom>Time Preferences</Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `80px repeat(${timeSlots.length}, 1fr)`,
                            gap: '2px',
                            overflowX: isMobile ? 'scroll' : 'unset'
                        }}
                    >
                        <Box></Box>
                        {timeSlots.map((slot, i) => (
                            <Box key={i} sx={{ textAlign: 'center', fontSize: 12, color: '#aaa' }}>{slot}</Box>
                        ))}
                        {days.map((day) => (
                            <React.Fragment key={day}>
                                <Box sx={{ fontSize: 13, fontWeight: 'bold', color: '#ccc' }}>{day}</Box>
                                {heatmapData[day].map((val, j) => (
                                    <Tooltip title={`${day}, ${timeSlots[j]}: ${val} users`} arrow key={`${day}-${j}`}>
                                        <Box
                                            sx={{
                                                height: 32,
                                                backgroundColor: getBlockColor(val),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 11,
                                                color: val >= 7 ? '#000' : '#eee',
                                                borderRadius: 1
                                            }}
                                        >
                                            {val > 0 ? val : ''}
                                        </Box>
                                    </Tooltip>
                                ))}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>

                <Box mb={3}>
                    <Typography variant="h6" gutterBottom>Top User Preferences</Typography>
                    <Table size="small" sx={{ backgroundColor: '#181818', borderRadius: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: '#ccc' }}>User</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>Days</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>Time</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>Source</TableCell>
                                <TableCell sx={{ color: '#ccc' }}>Zip</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dummyUsers.map((user, idx) => (
                                <TableRow key={idx}>
                                    <TableCell sx={{ color: '#eee' }}>{user.name}</TableCell>
                                    <TableCell sx={{ color: '#eee' }}>{user.days}</TableCell>
                                    <TableCell sx={{ color: '#eee' }}>{user.time}</TableCell>
                                    <TableCell sx={{ color: '#eee' }}>{user.source}</TableCell>
                                    <TableCell sx={{ color: '#eee' }}>{user.location}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">📍 Suggested Venues</Typography>
                        <Typography variant="body2" color="gray">
                            4 coaches nearby · 2 site managers nearby
                        </Typography>
                    </Box>

                    {[
                        {
                            name: 'Glendale Rec Center',
                            distance: '1.8 mi',
                            capacity: 45,
                            rating: 4.6,
                            rateRange: '$45–$75/hr',
                        },
                        {
                            name: 'Pasadena YMCA',
                            distance: '2.3 mi',
                            capacity: 30,
                            rating: 4.2,
                            rateRange: '$35–$60/hr',
                        },
                        {
                            name: 'Marshall HS Gym',
                            distance: '2.9 mi',
                            capacity: 60,
                            rating: null,
                            rateRange: '$40–$70/hr',
                        },
                    ].map((venue, idx) => (
                        <Box key={idx} mb={2} p={2} sx={{ backgroundColor: '#181818', borderRadius: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="subtitle1" sx={{ color: '#fff' }}>
                                        {venue.name}
                                    </Typography>
                                    <Typography variant="body2" color="gray">
                                        {venue.distance} · Capacity: {venue.capacity}
                                    </Typography>
                                    <Typography variant="body2" color="gray">
                                        Rating: {venue.rating ? `${venue.rating}★` : 'N/A — never used before'} · Rate: {venue.rateRange}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    sx={{
                                        color: '#ccc',
                                        borderColor: '#888',
                                        fontSize: '0.75rem',
                                        px: 2,
                                        height: '32px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Check Availability
                                </Button>
                            </Box>
                        </Box>
                    ))}
                </Box>


                <Divider sx={{ borderColor: '#444', my: 3 }} />

                <Box
                    display="flex"
                    flexDirection={isMobile ? 'column' : 'row'}
                    gap={2}
                    justifyContent="flex-end"
                >
                    <Button
                        variant="outlined"
                        fullWidth={isMobile}
                        sx={{ borderColor: '#888', color: '#ccc' }}
                        onClick={() => console.log('💾 Save Draft clicked')}
                    >
                        Save Draft
                    </Button>
                    <Button
                        variant="outlined"
                        fullWidth={isMobile}
                        sx={{ borderColor: '#888', color: '#ccc' }}
                        onClick={() => setProposeModalOpen(true)}
                    >
                        Propose Session
                    </Button>
                </Box>

                <ProposeSessionModal open={proposeModalOpen} onClose={() => setProposeModalOpen(false)} />
            </Box>
        </Box>
    );
};

export default ClusterInsightsExpanded;
